import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Plus, Tag, XCircle, Info, Calendar, Clock, CreditCard, Ticket, Users } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import { format } from "date-fns"

import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

interface Coupon {
  id: string
  code: string
  description: string | null
  discount_type: "percentage" | "fixed_amount" | "free_months"
  discount_value: string
  max_uses: number | null
  used_count: number
  max_uses_per_user: number
  valid_from: string | null
  valid_until: string | null
  is_active: boolean
  stripe_coupon_id: string | null
  stripe_promotion_code_id: string | null
  created_at: string
}

const createSchema = z.object({
  code: z.string().min(1).max(50),
  description: z.string().optional(),
  discount_type: z.enum(["percentage", "fixed_amount", "free_months"]),
  discount_value: z.preprocess((v) => Number(v), z.number().positive()),
  max_uses: z.preprocess((v) => (v === "" ? undefined : Number(v)), z.number().int().positive().optional()),
  max_uses_per_user: z.preprocess((v) => Number(v), z.number().int().positive()).default(1),
  valid_until: z.string().optional(),
  stripe_coupon_id: z.string().optional(),
  stripe_promotion_code_id: z.string().optional(),
})

type CreateForm = z.infer<typeof createSchema>

export default function AdminCouponsPage() {
  const qc = useQueryClient()
  const [sheetOpen, setSheetOpen] = useState(false)

  const { data: coupons = [], isLoading } = useQuery<Coupon[]>({
    queryKey: ["admin", "coupons"],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/coupons")
      return data.data ?? []
    },
  })

  // Separate active and inactive
  const activeCoupons = coupons.filter(c => c.is_active)
  const inactiveCoupons = coupons.filter(c => !c.is_active)

  const create = useMutation({
    mutationFn: async (body: CreateForm) => {
      const { data } = await api.post("/api/admin/coupons", {
        ...body,
        code: body.code.toUpperCase(),
        valid_until: body.valid_until ? new Date(body.valid_until).toISOString() : undefined,
      })
      return data.data
    },
    onSuccess: () => {
      toast.success("Marketing Campaign Created")
      qc.invalidateQueries({ queryKey: ["admin", "coupons"] })
      setSheetOpen(false)
      form.reset()
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error ?? "Failed to create coupon"
      toast.error(msg)
    },
  })

  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/admin/coupons/${id}`)
    },
    onSuccess: () => {
      toast.success("Coupon forcefully retired")
      qc.invalidateQueries({ queryKey: ["admin", "coupons"] })
    },
    onError: () => toast.error("Failed to retire coupon"),
  })

  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      discount_type: "percentage",
      max_uses_per_user: 1,
    },
  })

  function onSubmit(values: CreateForm) {
    create.mutate(values)
  }

  return (
    <div className="p-8 max-w-[1400px] mx-auto flex flex-col h-[calc(100vh-theme(spacing.16))]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Promotional Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Issue discount codes to drive sales. Links directly with the Stripe API.
          </p>
        </div>
        <Button onClick={() => setSheetOpen(true)} className="h-10 rounded-xl px-6 shadow-sm shadow-primary/20">
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign Code
        </Button>
      </div>

      <div className="flex gap-4 p-4 rounded-xl bg-indigo-50/50 border border-indigo-100 text-indigo-900 mb-8 w-fit shadow-sm">
        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
          <Info className="h-5 w-5 text-indigo-600" />
        </div>
        <div className="text-sm">
          <p className="font-bold mb-0.5 text-base">Stripe Synchronization Notice</p>
          <p className="text-indigo-800 break-words opacity-90 max-w-3xl">
            Coupons created here apply to the platform database. To allow customers to redeem codes during checkout, you <span className="font-semibold underline underline-offset-2">must</span> create the matching coupon in your Stripe Dashboard, and paste the <strong>Promotion Code ID</strong> (e.g., <code>promo_123</code>) into the field below.
          </p>
        </div>
      </div>

      <div className="flex-1 bg-card border border-border rounded-2xl shadow-sm overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto p-0">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card/95 backdrop-blur-sm z-10 border-b border-border shadow-sm">
              <tr>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Campaign Code</th>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Discount Configuration</th>
                <th className="text-center px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Utilization</th>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Validity Window</th>
                <th className="text-left px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider">Stripe Link</th>
                <th className="text-right px-6 py-4 font-semibold text-muted-foreground uppercase text-xs tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-5"><Skeleton className="h-12 w-48 rounded-xl" /></td>
                    <td className="px-6 py-5"><Skeleton className="h-8 w-24 rounded-lg" /></td>
                    <td className="px-6 py-5"><Skeleton className="h-8 w-16 mx-auto rounded-lg" /></td>
                    <td className="px-6 py-5"><Skeleton className="h-4 w-24" /></td>
                    <td className="px-6 py-5"><Skeleton className="h-6 w-32 rounded-md" /></td>
                    <td className="px-6 py-5"><Skeleton className="h-8 w-8 ml-auto rounded-md" /></td>
                  </tr>
                ))
              ) : coupons.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-24 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                        <Ticket className="h-8 w-8 text-slate-400" />
                      </div>
                      <p className="text-lg font-semibold text-foreground">No Marketing Campaigns</p>
                      <p className="text-sm mt-1 max-w-sm">You haven't issued any discount codes yet. Create a campaign to incentivize your users to upgrade.</p>
                      <Button onClick={() => setSheetOpen(true)} variant="outline" className="mt-6 rounded-xl">
                        <Plus className="h-4 w-4 mr-2" /> Start First Campaign
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                [...activeCoupons, ...inactiveCoupons].map((coupon) => {
                  const isActive = coupon.is_active
                  return (
                    <tr
                      key={coupon.id}
                      className={cn(
                        "group hover:bg-muted/40 transition-colors",
                        !isActive && "bg-muted/10 opacity-60"
                      )}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-4">
                          <div className={cn(
                            "h-10 w-10 mt-1 rounded-xl flex items-center justify-center shrink-0 border",
                            isActive ? "bg-emerald-50 border-emerald-200" : "bg-slate-100 border-slate-200"
                          )}>
                            <Tag className={cn("h-5 w-5", isActive ? "text-emerald-600" : "text-slate-400")} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-mono text-base font-bold text-foreground">
                                {coupon.code}
                              </p>
                              {!isActive && <Badge variant="secondary" className="text-[10px] uppercase">Retired</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 max-w-xs truncate" title={coupon.description || ""}>
                              {coupon.description || "No description provided"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="inline-flex flex-col gap-1 items-start bg-background border border-border px-3 py-1.5 rounded-lg shadow-sm">
                          <span className="font-bold text-lg leading-none">
                            {coupon.discount_type === "percentage" ? `${parseFloat(coupon.discount_value)}%` :
                              coupon.discount_type === "fixed_amount" ? `€${parseFloat(coupon.discount_value)}` :
                                `${parseFloat(coupon.discount_value)} Months`}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                            {coupon.discount_type === "percentage" ? "Off Total" :
                              coupon.discount_type === "fixed_amount" ? "Flat Discount" : "Free Access"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center justify-center">
                          <span className="font-bold text-lg">{coupon.used_count}</span>
                          <span className="text-xs text-muted-foreground">
                            {coupon.max_uses ? `/ ${coupon.max_uses} MAX` : "UNLIMITED"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm">
                          <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span className={cn(
                            "font-medium",
                            coupon.valid_until && new Date(coupon.valid_until) < new Date() && "text-red-500"
                          )}>
                            {coupon.valid_until ? format(new Date(coupon.valid_until), "MMM d, yyyy") : "Forever"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {coupon.stripe_promotion_code_id ? (
                          <div className="flex items-center gap-2 px-2 py-1 bg-indigo-50 rounded-md border border-indigo-100 w-fit">
                            <CreditCard className="h-3 w-3 text-indigo-500" />
                            <code className="text-[11px] font-mono font-medium text-indigo-700">
                              {coupon.stripe_promotion_code_id}
                            </code>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs italic">Not synched to Stripe</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {isActive && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-9 w-9 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                            onClick={() => {
                              if (confirm("Are you sure you want to retire this coupon? Users will no longer be able to claim it.")) {
                                deactivate.mutate(coupon.id)
                              }
                            }}
                            disabled={deactivate.isPending}
                            title="Retire Coupon"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-md w-full border-l border-border shadow-2xl p-0 flex flex-col">
          <SheetHeader className="p-6 border-b border-border bg-muted/20">
            <SheetTitle className="text-xl font-bold flex items-center gap-2">
              <Ticket className="h-5 w-5 text-indigo-500" />
              New Marketing Campaign
            </SheetTitle>
            <SheetDescription>
              Deploy a new discount code out into the wild.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 overflow-y-auto flex flex-col">
            <div className="p-6 space-y-6 flex-1">

              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Campaign Details</h3>

                <div>
                  <Label className="text-sm">Redemption Code</Label>
                  <Input
                    {...form.register("code")}
                    className="mt-1.5 h-12 text-lg font-mono font-bold tracking-wider uppercase rounded-xl border-dashed border-2 focus-visible:border-primary/50"
                    placeholder="BLACKFRIDAY2024"
                  />
                  {form.formState.errors.code && (
                    <p className="text-xs text-destructive mt-1 font-medium">{form.formState.errors.code.message}</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm text-balance">Internal Description</Label>
                  <Input {...form.register("description")} className="mt-1.5 h-10 rounded-xl" placeholder="50% off for early adopters via Twitter" />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Discount Logic</h3>
                <div className="grid grid-cols-5 gap-3">
                  <div className="col-span-3">
                    <Label className="text-sm">Modification Type</Label>
                    <Select
                      value={form.watch("discount_type")}
                      onValueChange={(v) => form.setValue("discount_type", v as CreateForm["discount_type"])}
                    >
                      <SelectTrigger className="mt-1.5 h-10 rounded-xl bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (%) Reduction</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount (€) Off</SelectItem>
                        <SelectItem value="free_months">Extended Free Trial (Months)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-sm">Face Value</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      {...form.register("discount_value")}
                      className="mt-1.5 h-10 rounded-xl font-bold"
                      placeholder={form.watch("discount_type") === "percentage" ? "50" : "15.00"}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Constraints & Limits</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5 bg-muted/30 p-3 rounded-xl border border-border/50">
                    <Label className="text-xs flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      Global Max Uses
                    </Label>
                    <Input type="number" min="1" {...form.register("max_uses")} className="h-9 rounded-lg bg-background" placeholder="∞" />
                  </div>
                  <div className="space-y-1.5 bg-muted/30 p-3 rounded-xl border border-border/50">
                    <Label className="text-xs flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      Expires On
                    </Label>
                    <Input type="date" {...form.register("valid_until")} className="h-9 rounded-lg bg-background text-sm" />
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t border-border pt-6">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-600 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Stripe Payment Integration
                </h3>
                <div className="space-y-4 p-4 rounded-xl bg-indigo-50/50 border border-indigo-100">
                  <div>
                    <Label className="text-xs font-semibold text-indigo-900">Promotion Code ID (Required for checkout)</Label>
                    <Input {...form.register("stripe_promotion_code_id")} className="mt-1.5 h-10 rounded-lg font-mono text-xs border-indigo-200 focus-visible:ring-indigo-500" placeholder="promo_..." />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-indigo-900">Base Coupon ID (Optional)</Label>
                    <Input {...form.register("stripe_coupon_id")} className="mt-1.5 h-10 rounded-lg font-mono text-xs border-indigo-200 focus-visible:ring-indigo-500" placeholder="coupon_..." />
                  </div>
                </div>
              </div>

            </div>

            <SheetFooter className="p-6 border-t border-border bg-muted/10 mt-auto">
              <Button type="button" variant="ghost" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={create.isPending} className="rounded-xl shadow-sm px-8 bg-indigo-600 hover:bg-indigo-700">
                {create.isPending ? "Generating..." : "Launch Campaign"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}
